/**
 * `AIRealtimeSkillCatalog.ts` — port of Unity's `AI/AIRealtimeSkillCatalog.cs`.
 *
 * Loads skills from `/AI/skills/index.json` (+ the referenced `*.md` files), sorted by
 * displayName (case-insensitive). Loader only — the interface model owns which skills are
 * available/loaded for the active role.
 *
 * Web deviation: the manifest is REQUIRED (like Unity's Android path) — the browser cannot
 * directory-scan the public root, so a skill file not listed in `index.json` is invisible.
 */

import { AIRealtimeSkillDefinition } from "./aiRealtimeContentTypes";
import { splitList, tryParseFrontmatter } from "./aiRealtimeMarkdownFrontmatter";
import { publicUrl } from "@core/publicUrl";
import { normalizeAgentPromptFileName } from "@models/data/agentPromptsDto";
import type { AgentPromptSource } from "./IAIPromptStoreModel";

const K_SKILLS_ROOT = publicUrl("AI/skills");
const K_MANIFEST_PATH = `${K_SKILLS_ROOT}/index.json`;

interface SkillManifestEntry {
  readonly id?: string;
  readonly displayName?: string;
  readonly description?: string;
  readonly file?: string;
  readonly roles?: string[];
  readonly role?: string;
  readonly tools?: string[];
}

/**
 * @param prompts The deployment's PUBLISHED skill files (see
 * `IAIPromptStoreModel`), which stand in for the copy that shipped in the build.
 * The MANIFEST is never publishable — which skills
 * exist, and their ids, stay the shipped ones — so a published file can change a
 * skill's guidance but can never add, remove, or rename one.
 *
 * **`description` is FRONTMATTER-AUTHORITATIVE** (2026-08-14) — see the same
 * note on `loadAvailableRolesAsync`. It matters more here than there: this text
 * is the ONLY thing the agent is told about an unloaded skill, so a description
 * that could not be edited was a prompt surface with no way to fix it. `roles:`
 * deliberately stays manifest-owned — it is a gating set, and the account
 * already has its own per-role scope switch on the Settings tab.
 *
 * @returns the skills, or **null when the manifest or one of its referenced
 * files could not be read** — see the note on `loadAvailableRolesAsync`; the
 * caller keeps its previous catalog rather than dropping loaded skills on a
 * transient or partial read failure.
 */
export async function loadAvailableSkillsAsync(
  prompts?: AgentPromptSource | null,
): Promise<AIRealtimeSkillDefinition[] | null> {
  let entries: SkillManifestEntry[];
  try {
    // `no-cache` so a Reset re-reads `public/AI/**` from the server rather than a
    // memory-cached copy from earlier in the same session.
    const response = await fetch(K_MANIFEST_PATH, { cache: "no-cache" });
    if (!response.ok) {
      console.warn(`[AIRealtimeSkillCatalog] Failed to read ${K_MANIFEST_PATH}: HTTP ${response.status}`);
      return null;
    }

    const manifest = (await response.json()) as { skills?: SkillManifestEntry[] };
    entries = manifest.skills ?? [];
  } catch (e) {
    console.warn(`[AIRealtimeSkillCatalog] Failed to parse ${K_MANIFEST_PATH}:`, e);
    return null;
  }

  const skills: AIRealtimeSkillDefinition[] = [];
  for (const entry of entries) {
    if (!entry || !entry.file || !entry.file.trim()) {
      continue;
    }

    const skill = await tryLoadSkillAsync(entry, prompts);
    if (!skill) return null;
    skills.push(skill);
  }

  skills.sort((left, right) =>
    left.displayName.toLowerCase() < right.displayName.toLowerCase()
      ? -1
      : left.displayName.toLowerCase() > right.displayName.toLowerCase()
        ? 1
        : 0,
  );
  return skills;
}

async function tryLoadSkillAsync(
  entry: SkillManifestEntry,
  prompts?: AgentPromptSource | null,
): Promise<AIRealtimeSkillDefinition | null> {
  const fileName = normalizeAgentPromptFileName(entry.file);
  if (!fileName) {
    return null;
  }

  // Keyed on the BASENAME, the same value the fetch below uses — a manifest
  // entry may carry a path, and keying the published text on the raw string would miss
  // for exactly those entries.
  const published = prompts?.getPromptText("skills", fileName) ?? null;

  let markdown: string;
  if (published !== null) {
    markdown = published;
  } else {
    try {
      const response = await fetch(`${K_SKILLS_ROOT}/${fileName}`, { cache: "no-cache" });
      if (!response.ok) {
        console.warn(`[AIRealtimeSkillCatalog] Failed to read '${K_SKILLS_ROOT}/${fileName}': HTTP ${response.status}`);
        return null;
      }

      markdown = await response.text();
    } catch (e) {
      console.warn(`[AIRealtimeSkillCatalog] Failed to read '${K_SKILLS_ROOT}/${fileName}':`, e);
      return null;
    }
  }

  if (!markdown.trim()) {
    return null;
  }

  const { fields, body } = tryParseFrontmatter(markdown);
  const baseName = fileName.replace(/\.md$/i, "");

  const frontmatterDescription = firstNonEmpty(fields["description"], fields["when_to_use"]);
  const frontmatterRoles = firstNonEmpty(fields["roles"], fields["role"]);

  const displayName = firstNonEmpty(
    entry.displayName,
    fields["name"],
    fields["displayname"],
    extractDisplayName(body, baseName),
  ) ?? "";
  const description = firstNonEmpty(entry.description, frontmatterDescription, extractDescription(body));
  const rawId = firstNonEmpty(entry.id, fields["id"], baseName);
  const id = normalizeSkillId(rawId ?? "");
  const manifestRoles = buildManifestRoles(entry);
  const roles = manifestRoles.length > 0 ? manifestRoles : splitList(frontmatterRoles);
  const manifestTools = (entry.tools ?? []).filter((tool) => tool && tool.trim());
  const tools = manifestTools.length > 0 ? manifestTools : splitList(fields["tools"]);

  return new AIRealtimeSkillDefinition(
    id,
    displayName,
    fileName,
    body,
    description,
    roles,
    tools,
    markdown,
    published !== null,
  );
}

function buildManifestRoles(entry: SkillManifestEntry): string[] {
  const roles = (entry.roles ?? []).filter((role) => role && role.trim());
  if (roles.length === 0 && entry.role && entry.role.trim()) {
    return splitList(entry.role);
  }

  return roles;
}

function firstNonEmpty(...values: Array<string | undefined | null>): string | null {
  for (const value of values) {
    if (value && value.trim()) {
      return value.trim();
    }
  }

  return null;
}

/** First `#`-heading text in the markdown, else the humanized file base name. */
function extractDisplayName(markdown: string, fallbackBaseName: string): string {
  if (markdown.trim()) {
    for (const rawLine of markdown.split(/\r\n|\n/)) {
      const line = rawLine.trim();
      if (!line || !line.startsWith("#")) {
        continue;
      }

      const heading = line.replace(/^[#\s\t]+/, "");
      if (heading.trim()) {
        return heading.trim();
      }
    }
  }

  return humanizeIdentifier(fallbackBaseName);
}

/** First non-blank, non-heading line of the markdown. */
function extractDescription(markdown: string): string | null {
  if (!markdown.trim()) {
    return null;
  }

  for (const rawLine of markdown.split(/\r\n|\n/)) {
    const line = rawLine.trim();
    if (!line || line.startsWith("#")) {
      continue;
    }

    return line;
  }

  return null;
}

/**
 * Lower-case, non-alphanumerics collapsed to single `-`, trimmed of leading/trailing `-`.
 *
 * Exported for the divergence check, which has to compare an uploaded
 * file's `id:` against the built one through the SAME normalization — a raw
 * compare reports `Build Report` vs `build-report` as a divergence that isn't one.
 */
export function normalizeSkillId(rawId: string): string {
  if (!rawId.trim()) {
    return "";
  }

  const trimmed = rawId.trim();
  let result = "";
  let lastWasSeparator = false;
  for (const character of trimmed) {
    if (/[\p{L}\p{N}]/u.test(character)) {
      result += character.toLowerCase();
      lastWasSeparator = false;
    } else if (!lastWasSeparator) {
      result += "-";
      lastWasSeparator = true;
    }
  }

  return result.replace(/^-+|-+$/g, "");
}

function humanizeIdentifier(rawIdentifier: string): string {
  if (!rawIdentifier.trim()) {
    return "Skill";
  }

  const tokens = rawIdentifier
    .replace(/[_-]/g, " ")
    .split(" ")
    .filter((token) => token.length > 0);

  if (tokens.length === 0) {
    return rawIdentifier.trim();
  }

  return tokens
    .map((token) => (token.length === 1 ? token.toUpperCase() : token[0].toUpperCase() + token.substring(1)))
    .join(" ");
}
